import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import InvestorPageShell from "../../../components/Investor/InvestorPageShell";
import KycBanner from "../../../components/Investor/KycBanner";
import MarketplaceOrderSummaryRail from "../../../components/Investor/MarketplaceOrderSummaryRail";
import MarketplaceOrderModal from "../../../components/Investor/MarketplaceOrderModal";
import MarketplaceProductImage from "../../../components/Investor/MarketplaceProductImage";
import InvestmentFaqList from "../../../components/Investor/InvestmentDetail/InvestmentFaqList";
import InvestorStatusBadge from "../../../components/Investor/InvestorStatusBadge";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { CardComp } from "../../../components";
import { useInvestorGate } from "../../../hooks/useInvestorGate";
import { useInvestorSession } from "../../../hooks/useInvestorSession";
import { useInvestorModuleAuth } from "../../../hooks/useInvestorModuleAuth";
import { useGetMarketplaceProductQuery } from "../../../store/investor/investorMarketplaceApi";
import { useGetStaffMarketplaceProductPreviewQuery } from "../../../store/admin/marketplace/staffMarketplaceApi";
import { mapMarketplaceProductDetail } from "../../../utils/investorMarketplaceAdapter";
import { formatMoney } from "../../../utils/currencyFormat";
import { convertToTitleCase } from "../../../utils/helpers";
import {
  InvestorApiErrorState,
  InvestorEmptyState,
} from "../../../components/Investor/InvestorPlatformState";

export default function InvestorMarketplaceProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isStaffPreview = searchParams.get("preview") === "staff";
  const { ensureOrderAccess, kycStatus } = useInvestorGate();
  const { profile } = useInvestorSession();
  const { isStaff } = useInvestorModuleAuth();
  const [showOrderModal, setShowOrderModal] = useState(false);

  const slug = productId;

  const {
    data: apiProduct,
    isFetching: isLoadingApi,
    isError: isApiError,
    error: apiError,
    refetch,
  } = useGetMarketplaceProductQuery(slug, {
    skip: !slug || isStaffPreview,
  });

  const {
    data: staffPreviewRaw,
    isFetching: isLoadingPreview,
    isError: isPreviewError,
    refetch: refetchPreview,
  } = useGetStaffMarketplaceProductPreviewQuery(slug, {
    skip: !slug || !isStaffPreview || !isStaff,
  });

  const product = useMemo(() => {
    if (isStaffPreview && staffPreviewRaw) {
      return mapMarketplaceProductDetail(staffPreviewRaw);
    }
    return apiProduct || null;
  }, [isStaffPreview, staffPreviewRaw, apiProduct]);

  const detailPath = `/invest/marketplace/${slug}`;
  const isLoading = isStaffPreview ? isLoadingPreview : isLoadingApi;
  const isError = isStaffPreview ? isPreviewError || (isStaffPreview && !isStaff) : isApiError;
  const isNotFound =
    !isLoading &&
    (apiError?.status === 404 || (!product && !isLoading && (isError || !isStaffPreview)));

  const openOrder = () => {
    if (!product) return;
    if (!ensureOrderAccess(detailPath, { intent: "order", itemId: slug })) {
      toast("Complete KYC to place an order");
      return;
    }
    setShowOrderModal(true);
  };

  if (isLoading) {
    return (
      <InvestorPageShell
        title="Product"
        backTo="/invest/marketplace"
        backLabel="Back to marketplace"
      >
        <TableSkeletonLoader rows={6} columns={2} />
      </InvestorPageShell>
    );
  }

  if (isStaffPreview && !isStaff) {
    return (
      <InvestorPageShell
        title="Product"
        backTo="/invest/marketplace"
        backLabel="Back to marketplace"
      >
        <InvestorApiErrorState
          message="Staff preview requires a staff session. Sign in as staff to preview this product."
          onRetry={() => navigate("/login")}
        />
      </InvestorPageShell>
    );
  }

  if (isError && !product) {
    return (
      <InvestorPageShell
        title="Product"
        backTo="/invest/marketplace"
        backLabel="Back to marketplace"
      >
        {isNotFound || apiError?.status === 404 ? (
          <InvestorEmptyState
            title="Product not found"
            message="This product may have been unpublished or the link is incorrect."
          />
        ) : (
          <InvestorApiErrorState
            message="We couldn’t load this product. Please try again."
            onRetry={() => (isStaffPreview ? refetchPreview() : refetch())}
          />
        )}
      </InvestorPageShell>
    );
  }

  if (!product) {
    return (
      <InvestorPageShell
        title="Product"
        backTo="/invest/marketplace"
        backLabel="Back to marketplace"
      >
        <InvestorEmptyState
          title="Product not found"
          message="This product may have been unpublished or the link is incorrect."
        />
      </InvestorPageShell>
    );
  }

  const stockLabel = convertToTitleCase(product.stock?.replace(/_/g, " ") || "in stock");
  const whatYouGet = Array.isArray(product.whatYouGet) ? product.whatYouGet : [];
  const faqs = Array.isArray(product.faqs) ? product.faqs : [];
  const deliveryRegions = Array.isArray(product.deliveryRegions) ? product.deliveryRegions : [];

  return (
    <InvestorPageShell
      title={product.name}
      subtitle={product.category}
      backTo="/invest/marketplace"
      backLabel="Back to marketplace"
      status={kycStatus !== "approved" ? kycStatus : null}
      statusLabel={kycStatus !== "approved" ? "KYC required" : null}
    >
      {isStaffPreview ? (
        <div
          className="alert mb-3 py-2 px-3 small"
          style={{
            background: "rgba(49, 119, 140, 0.1)",
            border: "1px solid rgba(49, 119, 140, 0.35)",
            color: "#31778c",
          }}
          role="status"
        >
          Staff preview — live content from Marketplace Admin. Investors see this after publish.
        </div>
      ) : null}

      <KycBanner status={kycStatus} redirectTo={detailPath} intent="order" itemId={slug} />

      <div className="row g-4">
        <div className="col-lg-8">
          <MarketplaceProductImage product={product} />

          <div className="d-flex flex-wrap align-items-center gap-2 mt-3 mb-2">
            <InvestorStatusBadge status={product.stock || "in_stock"} label={stockLabel} />
            {product.stockQuantity != null ? (
              <span className="text-muted small">Stock: {product.stockQuantity}</span>
            ) : null}
          </div>

          <h4 className="fw-bold mb-2" style={{ color: "#383838" }}>
            {product.name}
          </h4>
          <p className="fw-bold mb-3" style={{ color: "var(--color-primary)", fontSize: "1.25rem" }}>
            {formatMoney(product.priceNgn)}{" "}
            <span className="text-muted fw-normal small">{product.unit}</span>
          </p>

          {product.description ? (
            <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
              <h6 className="fw-semibold mb-2">Overview</h6>
              <p className="text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {product.description}
              </p>
            </CardComp>
          ) : null}

          {product.fullDescription ? (
            <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
              <h6 className="fw-semibold mb-2">Details</h6>
              <p className="text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {product.fullDescription}
              </p>
            </CardComp>
          ) : null}

          {whatYouGet.length > 0 ? (
            <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
              <h6 className="fw-semibold mb-2">What you get</h6>
              <ul className="mb-0 ps-3">
                {whatYouGet.map((item, idx) => (
                  <li key={idx} className="text-muted mb-1">
                    {typeof item === "string" ? item : item?.text || item?.title || ""}
                  </li>
                ))}
              </ul>
            </CardComp>
          ) : null}

          <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
            <h6 className="fw-semibold mb-2">Delivery & pickup</h6>
            <ul className="list-unstyled mb-0 small text-muted">
              <li className="mb-1">
                Delivery: {product.deliveryAvailable ? "Available" : "Not available"}
              </li>
              <li className="mb-1">
                Pickup: {product.pickupAvailable ? "Available" : "Not available"}
              </li>
              {product.pickupLocations?.length ? (
                <li className="mb-1">
                  Pickup locations: {product.pickupLocations.join(" · ")}
                </li>
              ) : product.pickupLocation ? (
                <li className="mb-1">Pickup location: {product.pickupLocation}</li>
              ) : null}
              {product.estimatedFulfillmentTime ? (
                <li className="mb-1">Estimated fulfillment: {product.estimatedFulfillmentTime}</li>
              ) : null}
              {deliveryRegions.length > 0 ? (
                <li className="mb-0">Regions: {deliveryRegions.join(", ")}</li>
              ) : null}
            </ul>
          </CardComp>

          {product.qualityAssurance ? (
            <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
              <h6 className="fw-semibold mb-2">Quality assurance</h6>
              <p className="text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {product.qualityAssurance}
              </p>
            </CardComp>
          ) : null}

          {faqs.length > 0 ? (
            <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
              <h6 className="fw-semibold mb-2">FAQs</h6>
              <InvestmentFaqList faqs={faqs} />
            </CardComp>
          ) : null}

          <p className="small text-muted mb-0">
            <Link to="/invest/marketplace" style={{ color: "var(--color-secondary)" }}>
              ← Back to marketplace
            </Link>
          </p>
        </div>

        <div className="col-lg-4">
          <MarketplaceOrderSummaryRail
            product={product}
            onOrder={
              isStaffPreview
                ? () => toast("Ordering is disabled in staff preview")
                : openOrder
            }
            loading={false}
          />
          {isStaffPreview ? (
            <p className="text-muted small mt-2 d-none d-lg-block">
              Ordering is disabled in staff preview.
            </p>
          ) : null}
        </div>
      </div>

      {!isStaffPreview ? (
        <MarketplaceOrderModal
          show={showOrderModal}
          product={product}
          profile={profile}
          onClose={() => setShowOrderModal(false)}
          onSuccess={(order) => {
            setShowOrderModal(false);
            if (order?.orderNumber) {
              navigate(`/invest/marketplace/orders/${encodeURIComponent(order.orderNumber)}`);
            }
          }}
        />
      ) : null}
    </InvestorPageShell>
  );
}
